import styled from "styled-components";
import { useState } from "react";
import { Check, X, Lock } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

// Blocking a room takes it off the OTA, and the next shift sees only that it is unavailable. A typed
// reason plus a free-text note is what makes a block readable at handover — "Persiapan transit 14:00"
// rather than a silent lock.
//
// Its own dialog rather than ConfirmDialog because the values have to reach onConfirm: ConfirmDialog's
// callback is captured when the dialog state is set, so it cannot see fields edited afterwards.
const REASONS = [
  { value: "transit_prep", labelKey: "block_reason_transit_prep" },
  { value: "maintenance", labelKey: "block_reason_maintenance" },
  { value: "ota_stop_sell", labelKey: "block_reason_ota_stop_sell" },
  { value: "owner_use", labelKey: "block_reason_owner_use" },
  { value: "other", labelKey: "block_reason_other" },
];

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 1.1rem;
  font-weight: 800;
  gap: 0.4rem;
  margin: 0 0 0.5rem;
`;

const Body = styled.div`
  color: ${theme.ink700};
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 0.9rem;
`;

const Field = styled.label`
  color: ${theme.muted};
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  font-weight: 700;
  gap: 0.25rem;
  margin-bottom: 0.8rem;
`;

const Select = styled.select`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.ink};
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.45rem 0.6rem;
`;

const TextArea = styled.textarea`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${theme.ink};
  font-size: 0.9rem;
  min-height: 68px;
  padding: 0.45rem 0.6rem;
  resize: vertical;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 0.35rem;
  padding: 0.5rem 1rem;
`;

const Cancel = styled(Button)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Ok = styled(Button)`
  background: ${theme.gold};
  border: 0;
  color: #ffffff;
`;

const BlockRoomDialog = (props) => {
  const { t } = useT();
  const [reason, setReason] = useState(props.reason || REASONS[0].value);
  const [note, setNote] = useState(props.note || "");

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Title>
          <Lock size={16} aria-hidden="true" />
          {props.editing ? t("edit_block_reason") : t("block_room")}{" "}
          {props.roomCode}
        </Title>
        <Body>
          {props.editing ? t("edit_block_reason_body") : t("confirm_block")}
        </Body>

        <Field>
          {t("block_reason_label")}
          <Select
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          >
            {REASONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          {t("block_note_label")}
          <TextArea
            value={note}
            placeholder={t("block_note_placeholder")}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        <Actions>
          <Cancel type="button" onClick={props.onCancel}>
            <X size={15} /> {t("confirm_cancel")}
          </Cancel>
          <Ok
            type="button"
            onClick={() => props.onConfirm({ reason: reason, note: note })}
          >
            <Check size={15} /> {t("confirm_ok")}
          </Ok>
        </Actions>
      </Card>
    </Backdrop>
  );
};

export default BlockRoomDialog;
